var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { ReservationSource } from "../../../models/reservation/reservation.model";
import { useOrganization } from "../../../state/auth.reducer";
import { useAllServices } from "../../../utils/hooks/services/useServices";
import { useCurrentSector } from "../../../utils/hooks/sector/useSectors";
import { useAllSectors } from "../../../utils/hooks/sectors/useSectors";
import CopyMenuButton from "../../CopyMenuButton";
import { getDisplayName } from "../../profileIdentity/profileIdentity.util";
var day = function (value) {
    if (!value)
        return undefined;
    var date = typeof value === 'string' ? DateTime.fromISO(value) : DateTime.fromJSDate(new Date(value));
    return date.isValid ? date : undefined;
};
var externalIds = function (entity) { var _a; return ((_a = entity.externalInfo) !== null && _a !== void 0 ? _a : []).map(function (info) { return "".concat(info.provider, " ").concat(info.id); }).join(', '); };
var FullProfileToolbar = function (_a) {
    var _b;
    var profile = _a.profile, reservation = _a.reservation, reservationId = _a.reservationId, onClose = _a.onClose;
    var t = useTranslation().t;
    var organization = useOrganization();
    var services = useAllServices();
    var sectors = useAllSectors();
    var currentSector = useCurrentSector();
    var bookingId = (_b = reservation === null || reservation === void 0 ? void 0 : reservation.id) !== null && _b !== void 0 ? _b : reservationId;
    var profileLink = "".concat(window.location.origin, "/system/profiles/").concat(profile.id);
    var summary = function () {
        var _a;
        var _b, _c, _d, _e;
        var sector = (_b = sectors.find(function (item) { return item.id === (reservation === null || reservation === void 0 ? void 0 : reservation.sectorId); })) !== null && _b !== void 0 ? _b : currentSector;
        var stay = (_c = profile.currentStay) !== null && _c !== void 0 ? _c : (_d = profile.activeStays) === null || _d === void 0 ? void 0 : _d[0];
        var stayDates = [day(stay === null || stay === void 0 ? void 0 : stay.startDay), day(stay === null || stay === void 0 ? void 0 : stay.endDay)].filter(Boolean).map(function (date) { return date.toFormat('dd.MM.yyyy'); });
        var service = services.find(function (item) { return item.id === (reservation === null || reservation === void 0 ? void 0 : reservation.serviceId); });
        var start = day(reservation === null || reservation === void 0 ? void 0 : reservation.expectedStartDate);
        var source = (_a = {},
            _a[ReservationSource.GUEST_APP] = t('Guest app'),
            _a[ReservationSource.BOOKING_ENGINE] = t('Booking engine'),
            _a[ReservationSource.DASHBOARD] = t('Dashboard'),
            _a[ReservationSource.PMS] = 'PMS',
            _a[ReservationSource.SYSTEM] = undefined,
            _a)[(_e = reservation === null || reservation === void 0 ? void 0 : reservation.source) !== null && _e !== void 0 ? _e : ReservationSource.SYSTEM];
        var lines = [
            [t('Organization'), organization && [organization.name, organization.id && "(".concat(organization.id, ")")].filter(Boolean).join(' ')],
            [t('Sector'), sector === null || sector === void 0 ? void 0 : sector.name],
            [t('Guest'), [getDisplayName(profile), profile.id].filter(Boolean).join(' · ')],
            [t('PMS profile'), externalIds(profile)],
            [t('Stay'), stayDates.join(' – ')],
            [t('Booking'), [service === null || service === void 0 ? void 0 : service.name, start === null || start === void 0 ? void 0 : start.toFormat('dd.MM.yyyy HH:mm')].filter(Boolean).join(' · ')],
            [t('Reservation'), [bookingId, (reservation === null || reservation === void 0 ? void 0 : reservation.code) && "".concat(t('Code'), " ").concat(reservation.code), source].filter(Boolean).join(' · ')],
            [t('PMS reservation'), reservation && externalIds(reservation)],
            [t('Link'), profile.id && profileLink],
            [t('Page'), window.location.href],
        ];
        return lines.filter(function (_a) {
            var value = _a[1];
            return !!value;
        }).map(function (_a) {
            var label = _a[0], value = _a[1];
            return "".concat(label, ": ").concat(value);
        }).join('\n');
    };
    var entries = [
        { label: t('Copy summary'), Icon: SummarizeIcon, text: summary },
        profile.id && { label: t('Copy profile ID'), Icon: PersonIcon, text: function () { return profile.id; } },
        bookingId && { label: t('Copy reservation ID'), Icon: EventIcon, text: function () { return bookingId; } },
        profile.id && { label: t('Copy link'), Icon: LinkIcon, text: function () { return profileLink; } },
    ].filter(Boolean);
    return (_jsxs(Stack, __assign({ direction: 'row', gap: '4px', sx: {
            justifyContent: 'flex-end',
            p: 1,
            position: { md: 'absolute' },
            top: { md: 8 },
            right: { md: 16 },
            zIndex: 3,
            bgcolor: 'background.paper',
            borderRadius: '8px'
        } }, { children: [_jsx(CopyMenuButton, { entries: entries }), onClose && (_jsx(Tooltip, __assign({ title: t('Close'), arrow: true }, { children: _jsx(IconButton, __assign({ "aria-label": t('Close'), onClick: onClose, sx: { width: 36, height: 36, color: 'text.secondary' } }, { children: _jsx(CloseIcon, { sx: { fontSize: 20 } }) })) })))] })));
};
export default FullProfileToolbar;
